import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from "@angular/router";
import 'rxjs/add/operator/map';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { IdToken, LoginResponse } from '../models/login-response.model';
import { PermissionValues } from '../models/permission.model';
import { User } from '../models/user.model';
import { AccountEndpoint } from './account-endpoint.service';
import { ConfigurationService } from './configuration.service';
import { DBkeys } from './db-Keys';
import { EndpointFactory } from './endpoint-factory.service';
import { JwtHelper } from './jwt-helper';
import { LocalStoreManager } from './local-store-manager.service';
import { Utilities } from './utilities';


@Injectable()
export class AuthService {

    public get loginUrl() { return this.configurations.loginUrl; }
    public get homeUrl() { return this.configurations.homeUrl; }

    public loginRedirectUrl: string;
    public logoutRedirectUrl: string;

    public reLoginDelegate: () => void;

    private previousIsLoggedInCheck = false;
    private _loginStatus = new Subject<boolean>();
    applicationVersionName: any;

    constructor(private router: Router,
        private configurations: ConfigurationService,
        private endpointFactory: EndpointFactory,
        private localStorage: LocalStoreManager,
        private accountEndpoint: AccountEndpoint
        ) {
        this.initializeLoginStatus();
    }


    private initializeLoginStatus() {
        this.localStorage.getInitEvent().subscribe(() => {
            this.reevaluateLoginStatus();
        });
        this.accountEndpoint.getApplicationVersionName().subscribe(res => {
            this.applicationVersionName = res;           
        });    
    }


    gotoPage(page: string, preserveParams = true) {

        let navigationExtras: NavigationExtras = {
            queryParamsHandling: preserveParams ? "merge" : "", preserveFragment: preserveParams
        };


        this.router.navigate([page], navigationExtras);
    }


    redirectLoginUser() {
        let redirect = this.loginRedirectUrl && this.loginRedirectUrl != '/' && this.loginRedirectUrl != ConfigurationService.defaultHomeUrl ? this.loginRedirectUrl : this.homeUrl;
        this.loginRedirectUrl = null;


        let urlParamsAndFragment = Utilities.splitInTwo(redirect, '#');
        let urlAndParams = Utilities.splitInTwo(urlParamsAndFragment.firstPart, '?');

        let navigationExtras: NavigationExtras = {
            fragment: urlParamsAndFragment.secondPart,
            queryParams: Utilities.getQueryParamsFromString(urlAndParams.secondPart),
            queryParamsHandling: "merge"
        };

        this.router.navigate([urlAndParams.firstPart], navigationExtras);
    }


    redirectLogoutUser() {
        let redirect = this.logoutRedirectUrl ? this.logoutRedirectUrl : this.loginUrl;
        this.logoutRedirectUrl = null;

        this.router.navigate([redirect]);
    }


    redirectForLogin() {
        this.loginRedirectUrl = this.router.url;
        this.router.navigate([this.loginUrl]);
    }


    reLogin() {

        this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);

        if (this.reLoginDelegate) {
            this.reLoginDelegate();
        } else {
            this.redirectForLogin();
        }
    }


    refreshLogin() {
        return this.endpointFactory.getRefreshLoginEndpoint<LoginResponse>()
            .map(response => this.processLoginResponse(response, this.rememberMe));
    }


    login(userName: string, password: string, rememberMe?: boolean) {

        if (this.isLoggedIn)
            this.logout();

        return this.endpointFactory.getLoginEndpoint<LoginResponse>(userName, password)
            .map(response => this.processLoginResponse(response, rememberMe));
    }

    redirectToPreviousUserLogin() {
        
        return this.endpointFactory.getImpersonatePreviousLoginEndpoint<LoginResponse>(this.previousLoginUser)
            .map(response => this.processLoginResponse(response, false));
    }

    impersonatelogin(userName: string, currentLoginUser: string) {


        return this.endpointFactory.getImpersonateLoginEndpoint<LoginResponse>(userName, currentLoginUser)
            .map(response => this.processLoginResponse(response, false));
    }



    private processLoginResponse(response: LoginResponse, rememberMe: boolean) {

        let accessToken = response.access_token;

        if (accessToken == null)
            throw new Error("Received accessToken was empty");

        let idToken = response.id_token;
        let refreshToken = response.refresh_token || this.refreshToken;
        let expiresIn = response.expires_in;

        let tokenExpiryDate = new Date();
        tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + expiresIn);

        let accessTokenExpiry = tokenExpiryDate;

        let jwtHelper = new JwtHelper();
        let decodedIdToken = <IdToken>jwtHelper.decodeToken(response.id_token);

        let permissions: PermissionValues[] = Array.isArray(decodedIdToken.permission) ? decodedIdToken.permission : [decodedIdToken.permission];

        if (!this.isLoggedIn)
            this.configurations.import(decodedIdToken.configuration);



        let user = new User(
            decodedIdToken.sub,
            decodedIdToken.name,
            decodedIdToken.fullname,
            decodedIdToken.email,
            decodedIdToken.jobtitle,
            decodedIdToken.phone,
            Array.isArray(decodedIdToken.role) ? decodedIdToken.role : [decodedIdToken.role]);
        user.IsEnabled = true;

        this.saveCurrentPersonateUser(decodedIdToken.ispersonate, decodedIdToken.preusername);

        this.saveUserDetails(user, permissions, accessToken, idToken, refreshToken, accessTokenExpiry, rememberMe);
        this.reevaluateLoginStatus(user);

        return user;
    }


    private saveUserDetails(user: User, permissions: PermissionValues[], accessToken: string, idToken: string, refreshToken: string, expiresIn: Date, rememberMe: boolean) {

        if (rememberMe) {
            this.localStorage.savePermanentData(accessToken, DBkeys.ACCESS_TOKEN);
            this.localStorage.savePermanentData(idToken, DBkeys.ID_TOKEN);
            this.localStorage.savePermanentData(refreshToken, DBkeys.REFRESH_TOKEN);
            this.localStorage.savePermanentData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
            this.localStorage.savePermanentData(permissions, DBkeys.USER_PERMISSIONS);
            this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
        } else {
            this.localStorage.saveSyncedSessionData(accessToken, DBkeys.ACCESS_TOKEN);
            this.localStorage.saveSyncedSessionData(idToken, DBkeys.ID_TOKEN);
            this.localStorage.saveSyncedSessionData(refreshToken, DBkeys.REFRESH_TOKEN);
            this.localStorage.saveSyncedSessionData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
            this.localStorage.saveSyncedSessionData(permissions, DBkeys.USER_PERMISSIONS);
            this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
        }

        this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
    }

    private saveCurrentPersonateUser(isPersonate: string, preLoginUserName: string) {
        
        if (isPersonate == "1") {
            this.localStorage.savePermanentData(preLoginUserName, DBkeys.PREVIOUS_USER);
            this.localStorage.savePermanentData(isPersonate, DBkeys.IS_PERSONATE);
        }
    }

    public setExtraUserInfo(user: User, rememberMe: boolean) {
        this.accountEndpoint.getUserEndpoint<User>(user.Id).subscribe(userRes => this.saveExtraUserData(user, userRes, rememberMe));
    }

    private saveExtraUserData(user: User, userRes: User, rememberMe: boolean) {
        user.IsEnabled = userRes.IsEnabled;
        user.RoleId = userRes.RoleId;
        user.IsDeleted = userRes.IsDeleted;
        user.RegistrationDate = userRes.RegistrationDate;
        user.SubscriptionDate = userRes.SubscriptionDate;
        user.ExpireDate = userRes.ExpireDate;
        user.IsTrial = userRes.IsTrial;
        user.IsPermanent = userRes.IsPermanent;
        user.IsExtented = userRes.IsExtented;
        user.Customer = userRes.Customer;

        if (rememberMe) {
            this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
        } else {
            this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
        }
    }

    logout(): void {
       

        this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
        this.localStorage.deleteData(DBkeys.ID_TOKEN);
        this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);
        this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
        this.localStorage.deleteData(DBkeys.USER_PERMISSIONS);
        this.localStorage.deleteData(DBkeys.CURRENT_USER);
        this.localStorage.deleteData(DBkeys.PREVIOUS_USER);
        this.localStorage.deleteData(DBkeys.IS_PERSONATE);       

        this.configurations.clearLocalChanges();

        this.reevaluateLoginStatus();

        this.removeSessionData();      
        
    }

    removeSessionData() {

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.ELECTRICITYMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.ELECTRICITYMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.ELECTRICITYCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.ELECTRICITYCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTWOMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTWOMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTWOCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTWOCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.BELPEXMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.BELPEXMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.BELPEXCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.BELPEXCHARTLABELS);


        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COTWOMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COTWOMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COTWOCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COTWOCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COALMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COALMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COALCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COALCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.OILMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.OILMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.OILCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.OILCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTRIALMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTRIALMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTRIALCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTRIALCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.ELECTRICITYTRIALMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.ELECTRICITYTRIALMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.ELECTRICITYTRIALCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.ELECTRICITYTRIALCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTWOTRIALMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTWOTRIALMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTWOTRIALCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.GASTWOTRIALCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.BELPEXTRIALMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.BELPEXTRIALMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.BELPEXTRIALCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.BELPEXTRIALCHARTLABELS);


        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COTWOTRIALMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COTWOTRIALMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COTWOTRIALCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COTWOTRIALCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COALTRIALMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COALTRIALMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COALTRIALCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.COALTRIALCHARTLABELS);

        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.OILTRIALMAX);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.OILTRIALMIN);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.OILTRIALCHARTDATA);
        sessionStorage.removeItem(this.applicationVersionName + "_" + DBkeys.OILTRIALCHARTLABELS);

    }


    private reevaluateLoginStatus(currentUser?: User) {

        let user = currentUser || this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
        let isLoggedIn = user != null;

        if (this.previousIsLoggedInCheck != isLoggedIn) {
            setTimeout(() => {
                this._loginStatus.next(isLoggedIn);
            });
        }

        this.previousIsLoggedInCheck = isLoggedIn;
    }


    getLoginStatusEvent(): Observable<boolean> {
        return this._loginStatus.asObservable();
    }


    get currentUser(): User {

        let user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
        this.reevaluateLoginStatus(user);

        return user;
    }

    get previousLoginUser(): string {
        return this.localStorage.getDataObject<string>(DBkeys.PREVIOUS_USER);
    }

    get userPermissions(): PermissionValues[] {       
        return this.localStorage.getDataObject<PermissionValues[]>(DBkeys.USER_PERMISSIONS) || [];
    }

    get accessToken(): string {

        this.reevaluateLoginStatus();
        return this.localStorage.getData(DBkeys.ACCESS_TOKEN);
    }

    get accessTokenExpiryDate(): Date {

        this.reevaluateLoginStatus();
        return this.localStorage.getDataObject<Date>(DBkeys.TOKEN_EXPIRES_IN, true);
    }

    get isSessionExpired(): boolean {

        if (this.accessTokenExpiryDate == null) {
            return true;
        }

        return !(this.accessTokenExpiryDate.valueOf() > new Date().valueOf());
    }


    get idToken(): string {

        this.reevaluateLoginStatus();
        return this.localStorage.getData(DBkeys.ID_TOKEN);
    }

    get refreshToken(): string {

        this.reevaluateLoginStatus();
        return this.localStorage.getData(DBkeys.REFRESH_TOKEN);
    }

    get isLoggedIn(): boolean {
        return this.currentUser != null;
    }

    get rememberMe(): boolean {
        return this.localStorage.getDataObject<boolean>(DBkeys.REMEMBER_ME) == true;
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/services/auth.service.ts